import React, { useState, useEffect } from 'react';
import ClinicUserReminderSettingsForm from './ClinicUserReminderSettingsForm';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { useParams } from 'react-router-dom';
import Loading from '../../../../common/Loading';
import userService from '../../../../../services/user.service';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const REMINDER_ITEMS = [
    { key: 'apptReminder', label: 'Appointment Reminder' },
    { key: 'isPrebookedReminder', label: 'Prebooked Reminder' },
    { key: 'isFirstReminder', label: 'First Reminder' },
    { key: 'isSecondReminder', label: 'Second Reminder' },
    { key: 'isLastReminder', label: 'Last Reminder' },
    { key: 'isSameDayReminder', label: 'Same Day Reminder' },
    { key: 'isClReplacement', label: 'Contact Lens Replacement Reminder' },
    { key: 'isClReorder', label: 'Contact Lens Reorder' },
    { key: 'isBirthday', label: 'Birthday Reminder' },
    { key: 'isThankyou', label: 'Thank You Reminder' },
    { key: 'isWelcome', label: 'Welcome Reminder' },
    { key: 'isEmail', label: 'Email Reminder' },
    { key: 'isSms', label: 'SMS Reminder' },
    { key: 'isCall', label: 'Call Reminder' },
    { key: 'isGlPickup', label: 'Glasses Pickup' },
    { key: 'isClPickup', label: 'Contact Lens Pickup' },
    { key: 'isFollowup', label: 'Follow Up' },
    { key: 'isApptSummary', label: 'Appointment Summary' },
    { key: 'isClGlSummary', label: 'Contact Lens & Glass Summary' },
];

function UserReminderTab({ setActiveTab }) {
    const { clinicId } = useParams();
    const [copied, setCopied] = useState(false);
    const [reminderSettings, setReminderSettings] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const selecteduser = useEyeconxStore((state) => state.selectedUser);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        if (selecteduser) {
            getUserReminder();
        }
    }, [selecteduser]);

    const handleBack = () => {
        setActiveTab?.('clinic-users-tab');
    };

    const getUserReminder = () => {
        setIsLoading(true);
        userService
            .getUserReminderSettings(clinicId, selecteduser.id)
            .then((res) => {
                setIsError(false);
                setReminderSettings(res.data);
                setLastModified(res.data.modifiedAt);
            })
            .catch((err) => {
                setIsError(true);
                setErrorMessage(err.response?.data?.message || 'Error loading reminder settings');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const updateReminderSettings = (newSettings) => {
        setReminderSettings(newSettings);
        setLastModified(newSettings.modifiedAt);
        setIsEditing(false);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(String(selecteduser.id));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getStatusBadge = (value) => {
        if (typeof value === 'boolean') {
            return value ? (
                <span className="clinic-users-status-badge clinic-users-status-badge--active">
                    <i className="bi bi-check-circle"></i> Enabled
                </span>
            ) : (
                <span className="clinic-users-status-badge clinic-users-status-badge--inactive">
                    <i className="bi bi-x-circle"></i> Disabled
                </span>
            );
        }
        return <strong>{value}</strong>;
    };

    const getPreferenceText = (value) => {
        switch (value) {
            case 'CLINIC':
                return 'Clinic';
            case 'ECP':
                return 'ECP';
            case 'BOTH':
                return 'Both';
            default:
                return 'Not set';
        }
    };

    const getCallPreferenceText = (value) => {
        switch (value) {
            case 'DISABLED':
                return 'Disabled';
            case 'HOME_PHONE':
                return 'Home Phone';
            case 'CELL_PHONE':
                return 'Cell Phone';
            default:
                return 'Not set';
        }
    };

    if (!selecteduser) {
        return (
            <div className="notifications-panel">
                <div className="notifications-panel__body">
                    <div className="clinic-users-empty">
                        <i className="bi bi-alarm"></i>
                        No user selected. Return to the users list to choose an ECP.
                        <div className="mt-3">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleBack}>
                                Back to Users
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div>
                    <button type="button" className="clinic-users-back-link mb-2" onClick={handleBack}>
                        <i className="bi bi-arrow-left"></i>
                        Back to Users
                    </button>
                    <h5 className="clinic-users__title mb-1">
                        <i className="bi bi-alarm text-primary me-1"></i>
                        Reminder Settings — {selecteduser.firstName} {selecteduser.lastName}
                    </h5>
                    <p className="text-muted small mb-0">Configure reminder preferences for this ECP.</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border font-monospace small">ID: {selecteduser.id}</span>
                    <ActionTooltipButton
                        tooltip={copied ? 'Copied!' : 'Copy user ID'}
                        tooltipId="copy-user-id"
                        variant="outline-secondary"
                        size="sm"
                        onClick={copyToClipboard}
                    >
                        <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                    </ActionTooltipButton>
                </div>
            </div>

            {isError && !isLoading && (
                <div className="alert alert-danger border-0 shadow-sm mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {errorMessage}
                </div>
            )}

            {isLoading ? (
                <Loading />
            ) : isEditing ? (
                <ClinicUserReminderSettingsForm
                    reminderSettings={reminderSettings}
                    user={selecteduser}
                    updateSettings={updateReminderSettings}
                    onCancel={() => setIsEditing(false)}
                />
            ) : (
                <div className="row g-3">
                    <div className="col-lg-7">
                        <div className="notifications-panel">
                            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                                <span className="d-flex align-items-center gap-2">
                                    <i className="bi bi-bell"></i>
                                    Reminder Toggles
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <i className="bi bi-pencil me-1"></i>
                                    Edit
                                </button>
                            </div>
                            <div className="notifications-panel__body">
                                {lastModified && (
                                    <p className="text-muted small text-end mb-2">Last modified: {lastModified}</p>
                                )}
                                {REMINDER_ITEMS.map((item) => (
                                    <div key={item.key} className="clinic-users-reminder-row">
                                        <span>{item.label}</span>
                                        {getStatusBadge(reminderSettings[item.key])}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-5">
                        <div className="notifications-panel">
                            <div className="notifications-panel__header">
                                <i className="bi bi-sliders"></i>
                                Reminder Preferences
                            </div>
                            <div className="notifications-panel__body">
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Call Preference</span>
                                    <span className="notifications-info-value">{getCallPreferenceText(reminderSettings.callPreference)}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Appointment Summary</span>
                                    <span className="notifications-info-value">{getPreferenceText(reminderSettings.apptSummary)}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Reminder Time</span>
                                    <span className="notifications-info-value">{reminderSettings.reminderTime || 'Not set'}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Same Day Reminder Time</span>
                                    <span className="notifications-info-value">{reminderSettings.sameDayReminderTime || 'Not set'}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Days — Prebook</span>
                                    <span className="notifications-info-value">{reminderSettings.daysPrebookReminder ?? 'Not set'}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Days — First</span>
                                    <span className="notifications-info-value">{reminderSettings.daysFirstReminder ?? 'Not set'}</span>
                                </div>
                                <div className="notifications-info-row">
                                    <span className="notifications-info-label">Days — Second</span>
                                    <span className="notifications-info-value">{reminderSettings.daysSecondReminder ?? 'Not set'}</span>
                                </div>
                                <div className="notifications-info-row mb-0">
                                    <span className="notifications-info-label">Minutes Earlier</span>
                                    <span className="notifications-info-value">{reminderSettings.minutesToAppt ?? 0} mins</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UserReminderTab;
